import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const PLAN_NAMES = {
  sj_starter_monthly: "Starter",
  sj_portfolio_monthly: "Portfolio",
  sj_pro_monthly: "Pro",
};

export default function PaymentResult({ cancelled }) {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");
  const [kind, setKind] = useState(null);
  const [lookupKey, setLookupKey] = useState(null);
  const sessionId = sp.get("session_id");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setStatus("timeout"); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setKind(data.kind); setLookupKey(data.lookup_key);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("failed"); return; }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, cancelled]);

  const isSub = kind === "subscription";
  const planName = PLAN_NAMES[lookupKey] || "";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div data-testid="payment-result-card" className="bg-white border border-slate-200 rounded-2xl shadow-sm p-10 max-w-md w-full text-center">
        {status === "checking" && <><Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" /><h1 className="mt-4 font-head text-2xl font-semibold">Confirming payment…</h1><p className="mt-2 text-slate-500">Please wait a moment.</p></>}
        {status === "paid" && (isSub ? (
          <><CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" /><h1 data-testid="payment-success-title" className="mt-4 font-head text-2xl font-semibold">Subscription Activated</h1><p className="mt-2 text-slate-500">Your{planName ? ` ${planName}` : ""} plan is now active. Welcome to SJ Property Care!</p></>
        ) : (
          <><CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" /><h1 data-testid="payment-success-title" className="mt-4 font-head text-2xl font-semibold">Payment Successful</h1><p className="mt-2 text-slate-500">Your invoice has been marked as paid. Thank you!</p></>
        ))}
        {(status === "cancelled" || status === "failed" || status === "timeout") && <><XCircle className="w-12 h-12 text-rose-600 mx-auto" /><h1 className="mt-4 font-head text-2xl font-semibold">Payment {status === "cancelled" ? "Cancelled" : "Not Completed"}</h1><p className="mt-2 text-slate-500">No charge was made. You can try again from your {isSub ? "plans page" : "invoices"}.</p></>}
        <Button
          data-testid="payment-return-btn"
          className="mt-6 bg-blue-600 hover:bg-blue-700"
          onClick={() => navigate(isSub ? (status === "paid" ? "/app/client" : "/app/client/plans") : "/app/client/invoices")}
        >
          {isSub ? (status === "paid" ? "Go to Dashboard" : "Back to Plans") : "Back to Invoices"}
        </Button>
      </div>
    </div>
  );
}
